import { defineRecipe } from '@chakra-ui/react';

/**
 * Чип — короткий факт, статус или число поверх картинки.
 *
 * Заводится рецептом, а не компонентом, по той же причине, по которой рецептами живут input и
 * textarea: чип обязан быть частью темы, иначе он превращается в ещё один local component рядом с
 * теми, что уже разъехались. На момент написания в коде 184 места с `borderRadius="full"` и ни
 * одного общего примитива: типографика бейджей систематизирована (`textStyle="badge"`, `micro`),
 * а контейнер каждый экран лепит руками. Отсюда семь сортов пилюли на одной странице профиля.
 *
 * Три варианта закрывают всё, что реально встречается:
 *
 * - `fact` — нейтральный факт: анкета, интересы, контакты, «своя сессия». То, чем уже является
 *   `PlayerMetaChips`, общий у профиля и страницы заявки;
 * - `status` — состояние и только оно, семантическим цветом: идёт сейчас, мест почти нет;
 * - `media` — число поверх обложки: непрозрачная подложка, чтобы читалось на любом кадре.
 *
 * Фиолетового варианта нет намеренно. Акцент на странице значит «сюда можно нажать» и принадлежит
 * кнопке; чип нажимать нельзя, поэтому красить его в акцент — врать про интерактивность.
 *
 * Совпадение с смотрящим («у вас тоже») кодируется не цветом, а весом: `emphasis="strong"`.
 */
export const chipRecipe = defineRecipe({
  className: 'tp-chip',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 0,
  },
  variants: {
    variant: {
      fact: {
        // Полупрозрачная заливка, а не сплошной токен: сплошной bg.card совпадал с карточкой,
        // на которой чип и стоит (состав в диалоге сессии), и пилюля пропадала — оставались
        // иконка и текст, по которым не видно, что контакт можно нажать. Значение и его цена
        // расписаны у самого токена `bg.chip`.
        bgColor: 'bg.chip',
        // Подпись тише тега заявки, но ярче фона: 60% от яркости тега (`gray.200`) против
        // 86% на 0.80. Разница намеренная и смысловая — тег отвечает «по этому ищут», это
        // предмет сканирования; чип отвечает «вот факт о человеке», его читают, когда уже
        // остановились на карточке. Сравнявшись с тегом по яркости, чипы забирали внимание
        // у строки, ради которой карточку открывают.
        //
        // Ступень whiteAlpha, а не сплошной цвет: заливка чипа полупрозрачна и ложится на
        // разные поверхности (карточка ленты, страница, диалог сессии), и текст обязан вести
        // себя так же — сплошной токен рано или поздно совпадёт с фоном, на который чип попал.
        // На чипе это 6.3:1, с запасом над AA.
        color: 'whiteAlpha.700',
        // Иконка — сплошным цветом, а не прозрачной ступенью, которую она наследовала бы
        // от текста. При прозрачном цвете у иконки из нескольких штрихов (глобус в
        // чипе языков: экватор и меридиан пересекают контур) альфа на стыках складывается —
        // пересечение уезжает к белому, контур выглядит пятнистым.
        //
        // Селектор без `>`: иконка в чипе обёрнута во Flex ради выравнивания
        // (PlayerMetaChips), прямым потомком она не является.
        '& svg': { color: 'fg.icon' },
        borderRadius: 'full',
        textStyle: 'caption',
      },
      status: {
        // Заливка и текст берутся от colorPalette: <Chip variant="status" colorPalette="green" />.
        bgColor: 'colorPalette.500/16',
        color: 'colorPalette.300',
        borderRadius: 'full',
        textStyle: 'badge',
      },
      media: {
        // Плотнее и темнее остальных: под ним кадр игры, а не поверхность интерфейса.
        // Без backdropFilter — на бейдже 40×18 его не видно, а стоит он как полноценный слой.
        bgColor: 'blackAlpha.700',
        color: 'fg',
        borderRadius: 'sm',
        textStyle: 'badge',
        fontVariantNumeric: 'tabular-nums',
      },
    },
    size: {
      sm: { px: '1.5', py: '0.5' },
      // 28px — общая высота серой пилюли на сайте, та же, что у Tag size="lg" (slot-recipes).
      // py остаётся ради многострочных чипов (языки в PlayerMetaChips переносятся по словам).
      md: { px: '3', py: '1', minH: '7' },
      // Факт, ради которого экран открыли: дата и длительность в диалоге сессии, где они не
      // подпись к чему-то, а само содержание. Отсюда и кегль на ступень крупнее.
      //
      // Скругление здесь `l2`, а не капсула: у капсулы форма читается как метка-ярлык, и на
      // строке в 14px с иконкой она выглядит несоразмерно вытянутой. Это тот же чип, просто
      // крупный, поэтому вариант остаётся `fact`, а не заводится четвёртым сортом пилюли.
      lg: { px: '3', py: '1.5', gap: '2', borderRadius: 'l2', textStyle: 'title' },
    },
    /** Совпало со смотрящим — вес и яркость текста, а не отдельный цвет. */
    emphasis: {
      normal: {},
      strong: { color: 'fg', fontWeight: 'semibold' },
    },
  },
  defaultVariants: {
    variant: 'fact',
    size: 'md',
    emphasis: 'normal',
  },
});
